import React, { useRef, useEffect, useState } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';

interface MiniVideoPlayerProps {
  slotId: string;
  s3Url?: string;
  seekValue?: string; // Seek time in seconds as string
  previewTime?: number; // Added for scrubbing preview
  onClick: (slotId: string) => void;
  style?: React.CSSProperties; // For positioning
  song?: string; // Added prop for song name
  artist?: string; // Added prop for artist name
}

export const MiniVideoPlayer: React.FC<MiniVideoPlayerProps> = ({ 
  slotId,
  s3Url, 
  seekValue = '0', // Default seek to 0 if undefined
  previewTime, // New prop for scrubbing
  onClick, 
  style,
  song,
  artist 
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null); // Ref for interval

  const startTime = parseFloat(seekValue);
  const endTime = startTime + 5;

  // Effect for normal video playback
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !s3Url) {
      setIsPlaying(false);
      if (intervalRef.current) clearInterval(intervalRef.current); // Clear interval if no video
      return;
    }

    // --- Reset state on prop change ---
    setError(null);
    setIsPlaying(false);
    if (intervalRef.current) clearInterval(intervalRef.current);

    // --- Event Handlers ---
    const handleLoadedMetadata = () => {
      console.log(`[MiniPlayer ${slotId}] Metadata loaded. Seeking to ${startTime}`);
      // Only set to startTime if no previewTime is active
      if (previewTime === undefined) {
        video.currentTime = startTime;
      }
    };

    const handleCanPlay = async () => {
      // Only start playing if no previewTime is active
      if (previewTime === undefined) {
        console.log(`[MiniPlayer ${slotId}] Can play. Attempting autoplay...`);
        try {
          await video.play();
          setIsPlaying(true);
          console.log(`[MiniPlayer ${slotId}] Autoplay started.`);
        } catch (playError) {
          console.error(`[MiniPlayer ${slotId}] Autoplay failed:`, playError);
          setError('Autoplay blocked'); // Inform user if needed
          setIsPlaying(false);
        }
      }
    };

    const handleError = (e: Event) => {
      console.error(`[MiniPlayer ${slotId}] Video error:`, e);
      setError('Video load error');
      setIsPlaying(false);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };

    // --- Add Listeners ---
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);

    // --- Cleanup ---
    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      // Pause video on unmount
      if (video && !video.paused) {
         video.pause();
      }
    };

  }, [s3Url, startTime, slotId, previewTime]); // Re-run if URL, start time, or previewTime changes

  // Effect for scrubbing preview
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !s3Url) return;

    // If preview time is set, update video time and pause playback
    if (previewTime !== undefined) {
      // Pause any playback
      if (!video.paused) {
        video.pause();
        setIsPlaying(false);
      }
      
      // Set to preview time if the video is ready
      if (video.readyState >= 2) { // HAVE_CURRENT_DATA or better
        video.currentTime = previewTime;
      }
    } else if (video.paused) {
      // When preview ends (previewTime becomes undefined), 
      // start playing from current position or startTime
      
      // Make sure we're at the correct time position (using either current time or startTime)
      // This ensures if we're at a random scrubbed position, we use that as the start time
      // If no scrubbing happened, we use the original startTime
      const loopStartTime = video.currentTime; // Use current position as loop start
      
      video.play()
        .then(() => {
          setIsPlaying(true);
          console.log(`[MiniPlayer ${slotId}] Playback started after preview at time ${loopStartTime}`);
        })
        .catch(e => console.error(`[MiniPlayer ${slotId}] Error starting playback after preview:`, e));
    }
  }, [previewTime, s3Url, slotId, startTime]);

  // --- Loop Logic --- 
  useEffect(() => {
    const video = videoRef.current;
    // Only loop when playing
    if (isPlaying && video) {
      console.log(`[MiniPlayer ${slotId}] Starting loop interval. Seek position: ${startTime}`);
      
      // When the player starts or preview changes, update the loop boundaries
      // Use current video position as the loop start (works after scrubbing)
      const loopStart = video.currentTime;
      const loopEnd = loopStart + 5;
      
      console.log(`[MiniPlayer ${slotId}] Loop set from ${loopStart} to ${loopEnd} for ${slotId}`);
      
      // Clear any existing interval before setting a new one
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      
      intervalRef.current = setInterval(() => {
        // Check if video is still available
        if (!video || video.readyState < 2) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          return;
        }
        
        // Debug log to see current time - only show for specific debugging cases
        // Remove or comment out this code to stop the constant console messages
        // if (slotId === 'outro_video1') {
        //   console.log(`[MiniPlayer ${slotId}] Current time: ${video.currentTime}, Loop end: ${loopEnd}`);
        // }
        
        if (video.currentTime >= loopEnd) {
          console.log(`[MiniPlayer ${slotId}] Loop end reached (${video.currentTime.toFixed(2)} >= ${loopEnd.toFixed(2)}). Resetting to ${loopStart.toFixed(2)}`);
          video.currentTime = loopStart;
          // Ensure it keeps playing after reset
          if (video.paused) {
            video.play().catch(e => console.error(`[MiniPlayer ${slotId}] Error restarting play in loop:`, e));
          }
        }
      }, 100); // Check every 100ms
    } else {
      // Clear interval if not playing
      if (intervalRef.current) {
        console.log(`[MiniPlayer ${slotId}] Clearing loop interval.`);
        clearInterval(intervalRef.current);
      }
    }

    // Cleanup interval on isPlaying change or unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, slotId, startTime, previewTime, seekValue]); // Include seekValue in dependencies

  // Create tooltip content based on available information
  const getTooltipContent = () => {
    if (song && artist) {
      return `${song} - ${artist}`;
    } else if (song) {
      return song;
    } else if (artist) {
      return artist;
    } else if (s3Url) {
      // Extract filename from URL as fallback
      const filename = s3Url.split('/').pop() || '';
      // Remove any query parameters for cleaner display
      const cleanFilename = filename.split('?')[0];
      
      // If the filename looks like a UUID or hash, just show "Video" and the slot
      if (/^[a-f0-9-]{10,}$/i.test(cleanFilename)) {
        return `Video (${slotId})`;
      }
      
      return cleanFilename;
    }
    return slotId; // Fallback to slot ID if no other info available
  };

  // --- Render ---
  return (
    <Tooltip title={getTooltipContent()} arrow placement="top">
      <Box
        onClick={() => onClick(slotId)}
        sx={{
          ...style, // Apply positioning styles
          border: '2px solid white',
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          overflow: 'hidden', // Hide video overflow
          color: 'white',
        }}
      >
        {s3Url ? (
          <video
            ref={videoRef}
            src={s3Url}
            muted // Autoplay usually requires muted
            playsInline // Important for iOS
            preload="metadata" // Load enough to get duration and seek
            style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover', // Cover the container
                display: error ? 'none' : 'block' // Hide video on error
            }}
          />
        ) : null}
        {(!s3Url || error) && (
          <Typography variant="h4" component="span" sx={{ fontWeight: 'bold' }}>
            ?
          </Typography>
        )}
        {/* Optional: Display error message */} 
        {/* {error && !s3Url && <Typography variant="caption" sx={{ position: 'absolute', bottom: 0, left: 0, p: 0.5, backgroundColor: 'rgba(0,0,0,0.8)' }}>{error}</Typography>} */}
      </Box>
    </Tooltip>
  );
}; 